import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { DashboardShell } from 'components/DashboardShell';
import { ParcelCard } from 'components/ParcelCard';
import { TripCard } from 'components/TripCard';
import { MissionCard } from 'components/MissionCard';
import { EmptyState, ListSkeleton } from 'components/EmptyState';
import { useAuth } from 'contexts/AuthContext';
import { useLanguage } from 'lib/i18n';
import api from 'lib/api';
import { formatPrice } from 'lib/format';
import { Package, Plane, Wallet, ClipboardList, Plus, ArrowRight, Star, Search, Truck, BadgeCheck } from 'lucide-react';

export default function DashboardPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ deliveries: 0, trips: 0, missions: 0, dashboardTotalBalance: null });
  const [recentParcels, setRecentParcels] = useState([]);
  const [recentTrips, setRecentTrips] = useState([]);
  const [recentMissions, setRecentMissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const activeRole = localStorage.getItem('blablacolis_active_role') || (user?.roles?.[0]) || 'sender';

  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.get('/deliveries?mine=true').then(r => r.data).catch(() => []),
      api.get('/trips?mine=true').then(r => r.data).catch(() => []),
      api.get('/missions').then(r => r.data).catch(() => []),
      api.get('/transactions/connect/status').then(r => r.data).catch(() => null),
    ]).then(([deliveries, trips, missions, walletStatus]) => {
      setStats({
        deliveries: deliveries.length,
        trips: trips.length,
        missions: missions.length,
        dashboardTotalBalance: Number.isFinite(Number(walletStatus?.dashboard_total_balance))
          ? Number(walletStatus.dashboard_total_balance)
          : null,
      });
      setRecentParcels(deliveries.slice(0, 3));
      setRecentTrips(trips.slice(0, 3));
      setRecentMissions(missions.slice(0, 3));
      setLoading(false);
    });
  }, [user]);

  return (
    <DashboardShell>
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold">{t('dashboard.greeting')}, {user?.full_name?.split(' ')[0]}</h1>
        <p className="text-muted-foreground">{t('dashboard.summary')}</p>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard icon={Package} label={t('dashboard.parcelCount')} value={stats.deliveries} testid="stat-deliveries" />
        <StatCard icon={Plane} label={t('dashboard.tripCount')} value={stats.trips} testid="stat-trips" />
        <StatCard icon={ClipboardList} label={t('dashboard.missionCount')} value={stats.missions} testid="stat-missions" />
        <StatCard icon={Wallet} label={t('dashboard.balanceWithProcessing')} value={formatPrice(stats.dashboardTotalBalance ?? user?.wallet_balance ?? 0)} testid="stat-wallet" />
      </div>

      {/* Quick actions based on roles */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
        {(user?.roles || []).includes('sender') && (
          <ActionCard icon={Plus} title={t('dashboard.newDelivery')} desc={t('dashboard.newDeliveryDesc')} onClick={() => navigate('/deliveries/new')} testid="action-new-delivery" />
        )}
        {(user?.roles || []).includes('traveler') && (
          <ActionCard icon={Plane} title={t('dashboard.newTrip')} desc={t('dashboard.newTripDesc')} onClick={() => navigate('/trips/new')} testid="action-new-trip" />
        )}
        {(user?.roles || []).includes('sender') && (
          <ActionCard icon={Search} title={t('dashboard.search')} desc={t('dashboard.searchDesc')} onClick={() => navigate('/search?tab=trip')} testid="action-search-trips" />
        )}
        {((user?.roles || []).includes('traveler') || (user?.roles || []).includes('deliverer')) && (
          <ActionCard icon={Package} title={t('dashboard.available')} desc={t('dashboard.availableDesc')} onClick={() => navigate('/search?tab=parcel')} testid="action-browse-deliveries" />
        )}
        {(user?.roles || []).includes('deliverer') && (
          <ActionCard icon={Truck} title={t('dashboard.vehicles')} desc={t('dashboard.vehiclesDesc')} onClick={() => navigate('/vehicles')} testid="action-vehicles" />
        )}
        {(user?.roles || []).includes('deliverer') && !user?.deliverer_validated && (
          <ActionCard icon={BadgeCheck} title={t('dashboard.validation')} desc={t('dashboard.validationDesc')} onClick={() => navigate('/documents')} testid="action-documents" />
        )}
      </div>

      {/* Recent missions */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">{t('dashboard.recentMissions')}</h2>
          <Link to="/missions" className="text-sm text-primary hover:underline" data-testid="dashboard-missions-link">{t('dashboard.seeAll')} <ArrowRight size={14} className="inline" /></Link>
        </div>
        {loading ? <ListSkeleton /> : recentMissions.length === 0 ? (
          <EmptyState icon={ClipboardList} title={t('dashboard.noMissions')} description={t('dashboard.noMissionsDesc')} />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {recentMissions.map(m => <MissionCard key={m.id} mission={m} isDeliverer={m.deliverer_id === user.id} />)}
          </div>
        )}
      </section>

      {/* Recent deliveries */}
      {(user?.roles || []).includes('sender') && (
        <section className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">{t('dashboard.lastParcels')}</h2>
            <Link to="/deliveries" className="text-sm text-primary hover:underline">{t('dashboard.seeAll')} <ArrowRight size={14} className="inline" /></Link>
          </div>
          {loading ? <ListSkeleton /> : recentParcels.length === 0 ? (
            <EmptyState icon={Package} title={t('dashboard.noParcels')} description={t('dashboard.noParcelsDesc')} action={<Button onClick={() => navigate('/deliveries/new')}>{t('dashboard.newDelivery')}</Button>} />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {recentParcels.map(p => <ParcelCard key={p.id} parcel={p} />)}
            </div>
          )}
        </section>
      )}

      {/* Recent trips */}
      {(user?.roles || []).includes('traveler') && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">{t('dashboard.lastTrips')}</h2>
            <Link to="/trips" className="text-sm text-primary hover:underline">{t('dashboard.seeAll')} <ArrowRight size={14} className="inline" /></Link>
          </div>
          {loading ? <ListSkeleton /> : recentTrips.length === 0 ? (
            <EmptyState icon={Plane} title={t('dashboard.noTrips')} description={t('dashboard.noTripsDesc')} action={<Button onClick={() => navigate('/trips/new')}>{t('dashboard.newTrip')}</Button>} />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {recentTrips.map(t => <TripCard key={t.id} trip={t} />)}
            </div>
          )}
        </section>
      )}
    </DashboardShell>
  );
}

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-muted-foreground">{label}</span>
        <Icon size={16} className="text-muted-foreground" />
      </div>
      <div className="text-2xl font-bold" data-testid={testid}>{value}</div>
    </CardContent>
  </Card>
);

const ActionCard = ({ icon: Icon, title, desc, onClick, testid }) => (
  <button onClick={onClick} className="text-left" data-testid={testid}>
    <Card className="hover:shadow-md-custom hover:-translate-y-px transition-all duration-200 h-full">
      <CardContent className="p-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
          <Icon size={18} />
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-sm">{title}</div>
          <div className="text-xs text-muted-foreground">{desc}</div>
        </div>
        <ArrowRight size={16} className="ml-auto text-muted-foreground" />
      </CardContent>
    </Card>
  </button>
);
